import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { merchantAmount, paymentMatches, purchaseStatus, type VerifiedPayment } from "./paymentValidation";

async function verifyProvider(reference: string) {
  const key = process.env["PAYSTACK_SECRET_KEY"];
  if (!key) throw new Error("Payments are not configured.");
  const response = await fetch(`https://api.paystack.co/transaction/verify/${encodeURIComponent(reference)}`, {
    headers: { Authorization: `Bearer ${key}` }, signal: AbortSignal.timeout(20_000),
  });
  const payload = await response.json() as { status?: boolean; message?: string; data?: VerifiedPayment };
  if (!response.ok || !payload.status || !payload.data) {
    if (response.status === 400 && payload.message === "Transaction reference not found.") return null;
    throw new Error("Paystack could not verify this payment. Please try again.");
  }
  return payload.data;
}

export const verifyCreditPurchase = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { reference: string }) => z.object({ reference: z.string().min(1).max(200) }).parse(data))
  .handler(async ({ data, context }) => {
    const { data: purchase, error } = await context.supabase.from("credit_purchases")
      .select("reference,amount,currency,status,credits,user_id").eq("reference", data.reference).eq("user_id", context.userId).maybeSingle();
    if (error || !purchase) throw new Error("Purchase not found.");
    const money = { value: Number(purchase.amount) / 100, currency: String(purchase.currency).toUpperCase() };
    if (purchase.status === "paid") return { status: "paid", credits: purchase.credits, ...money };
    const tx = await verifyProvider(data.reference);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    if (tx?.status === "success") {
      if (!paymentMatches(tx, purchase)) throw new Error("Payment details do not match this purchase. Please contact support.");
      const { data: settled, error: settlementError } = await supabaseAdmin.rpc("settle_credit_purchase", {
        _reference: data.reference, _amount: merchantAmount(tx), _currency: tx.currency,
      });
      if (settlementError) throw new Error("Your payment was received, but credits could not be added. Please retry verification.");
      const result = settled as { status: string; credits: number };
      return { status: result.status, credits: result.credits, ...money };
    }
    const status = tx ? purchaseStatus(tx.status) : "not_found";
    const { error: updateError } = await supabaseAdmin.from("credit_purchases").update({ status })
      .eq("reference", data.reference).neq("status", "paid").neq("status", "refunded");
    if (updateError) throw new Error("Could not refresh the payment status.");
    return { status, credits: 0 };
  });

/** Admin reconciliation reads Paystack; it never treats a pending row as proof of payment. */
export const reconcilePendingPurchases = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: admin, error: roleError } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (roleError || admin !== true) throw new Error("Forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin.from("credit_purchases").select("reference,amount,currency,status,credits")
      .eq("status", "pending").order("created_at").limit(50);
    if (error) throw new Error("Could not load checkouts.");
    const counts: Record<string, number> = {};
    // Bounded concurrency keeps the provider review within request limits.
    for (let i = 0; i < (rows ?? []).length; i += 5) {
      await Promise.all((rows ?? []).slice(i, i + 5).map(async (purchase) => {
        try {
          const tx = await verifyProvider(purchase.reference);
          let status = tx ? purchaseStatus(tx.status) : "not_found";
          if (tx?.status === "success") {
            if (!paymentMatches(tx, purchase)) throw new Error("Payment mismatch");
            const { error: settleError } = await supabaseAdmin.rpc("settle_credit_purchase", {
              _reference: purchase.reference, _amount: merchantAmount(tx), _currency: tx.currency,
            });
            if (settleError) throw settleError;
            status = "paid";
          } else {
            const { error: updateError } = await supabaseAdmin.from("credit_purchases").update({ status })
              .eq("reference", purchase.reference).eq("status", "pending");
            if (updateError) throw updateError;
          }
          counts[status] = (counts[status] ?? 0) + 1;
        } catch { counts["verification_error"] = (counts["verification_error"] ?? 0) + 1; }
      }));
    }
    return { reviewed: rows?.length ?? 0, counts };
  });